import { PortCall } from '../types/ViewModel/PortCall';
import { Supplier } from '../types/ViewModel/Supplier';
import { Chemical } from '../types/ViewModel/TankCleaning/Chemical';
import { ChemicalCategory } from '../types/ViewModel/TankCleaning/ChemicalCategory';
import { ChemicalCategoryOrderReportItem } from '../types/ViewModel/TankCleaning/Order/ChemicalCategoryOrderReportItem';
import { ChemicalCategoryRobReportItem } from '../types/ViewModel/TankCleaning/ROB/ChemicalCategoryRobReportItem';
import { ChemicalOrderItem } from '../types/ViewModel/TankCleaning/Order/ChemicalOrderItem';
import { ChemicalOrderPortCall } from '../types/ViewModel/TankCleaning/Order/ChemicalOrderPortCall';
import { ChemicalOrderReport } from '../types/ViewModel/TankCleaning/Order/ChemicalOrderReport';
import { ChemicalRobItem } from '../types/ViewModel/TankCleaning/ROB/ChemicalRobItem';
import { ChemicalRobPortCall } from '../types/ViewModel/TankCleaning/ROB/ChemicalRobPortCall';
import { ChemicalRobReport } from '../types/ViewModel/TankCleaning/ROB/ChemicalRobReport';
import { ChemicalRobReportItem } from '../types/ViewModel/TankCleaning/ROB/ChemicalRobReportItem';
import { ChemicalRobLocationReportItem } from '../types/ViewModel/TankCleaning/ROB/ChemicalRobReportLocationItem';
import { Unit } from '../types/ViewModel/Unit';
import { ChemicalOrderUpdateReportData } from '../types/ViewModel/TankCleaning/Order/ChemicalOrderUpdateReportData';
import { ChemicalOrderStatusEnum } from '../enums/chemicalOrderStatusEnum';
import { UpdateOrderStatusEnum } from '../enums/updateOrderStatusEnum';
import { ChemicalRobUpdateReportData } from '../types/ViewModel/TankCleaning/ROB/ChemicalRobUpdateReportData';
import { ChemicalOrderPortCallBM } from '../types/BusinessModel/TankCleaning/ChemicalOrderPortCallBM';
import { ChemicalRobPortCallBM } from '../types/BusinessModel/TankCleaning/ChemicalRobPortCallBM';
import { createChemicalRobListBM } from '../factory/TankCleaning/ChemicalRobItemFactory';
import { RobLocation } from '../types/ViewModel/TankCleaning/ROB/RobLoction';
import { createChemicalOrderItemListBM } from '../factory/TankCleaning/ChemicalOrderItemFactory';
import { isDateBefore } from 'odfjell-date-time-utils/dateTimeUtils';

export default () => {
    const generateChemicalOrderReport = (
        chemicalOrderForPortCall: ChemicalOrderPortCall | null,
        chemicalOrderForPortCallList: ChemicalOrderPortCall[],
        chemicalList: Chemical[],
        chemicalCategoryList: ChemicalCategory[]
    ) => {
        const _chemicalCategoryOrderReportItems: ChemicalCategoryOrderReportItem[] = [];
        const previousOrderForPortCall = chemicalOrderForPortCallList
            .filter((c) => c.parentOrderId === chemicalOrderForPortCall?.chemicalOrderId)
            .sort((a, b) => (isDateBefore(a.createdUtc, b.createdUtc) ? -1 : 1))[0];

        chemicalCategoryList.forEach((chemicalCategory) => {
            const _chemicalsInCategoryList = chemicalList.filter((chemical) => chemical.categoryId === chemicalCategory.categoryId);

            const _chemicalCategoryOrderReportItem: ChemicalCategoryOrderReportItem = {
                category: chemicalCategory,
                chemicalOrderReportItems: getChemicalOrderReportItems(_chemicalsInCategoryList),
            };

            const _mappedChemicalReportItems: ChemicalOrderItem[] = _chemicalCategoryOrderReportItem.chemicalOrderReportItems.map(
                (chemicalOrderReportItem) => {
                    const savedChemicalOrderItem = chemicalOrderForPortCall?.chemicalOrderItemList.find(
                        (c) => c.chemicalId === chemicalOrderReportItem.chemicalId
                    );
                    const previousChemicalOrderItem = previousOrderForPortCall?.chemicalOrderItemList.find(
                        (c) => c.chemicalId === chemicalOrderReportItem.chemicalId
                    );
                    const chemical = chemicalList.find((c) => c.chemicalId === chemicalOrderReportItem.chemicalId);

                    return {
                        chemicalOrderItemId: 0,
                        chemicalOrderId: savedChemicalOrderItem?.chemicalOrderId ?? 0,
                        chemicalId: chemical?.chemicalId ?? 0,
                        chemical: chemical,
                        robValue: savedChemicalOrderItem?.robValue ?? 0,
                        previousOrderValue: previousChemicalOrderItem?.orderValue ?? 0,
                        orderValue: savedChemicalOrderItem?.orderValue ?? 0,
                        comment: savedChemicalOrderItem?.comment ?? '',
                    };
                }
            );

            _chemicalCategoryOrderReportItem.chemicalOrderReportItems = _mappedChemicalReportItems;

            _chemicalCategoryOrderReportItems.push(_chemicalCategoryOrderReportItem);
        });

        const _chemicalOrderReport: ChemicalOrderReport = {
            chemicalOrderId: chemicalOrderForPortCall?.chemicalOrderId ? chemicalOrderForPortCall?.chemicalOrderId : null,
            portCallId: chemicalOrderForPortCall?.portCallId ? chemicalOrderForPortCall?.portCallId : null,
            imoNumber: chemicalOrderForPortCall?.imoNumber ? chemicalOrderForPortCall?.imoNumber : null,
            chemicalCategoryOrderReportItems: _chemicalCategoryOrderReportItems,
        };

        return _chemicalOrderReport;
    };

    const generateChemicalOrderUpdateReportData = (
        chemicalOrderReport: ChemicalOrderReport,
        chemicalOrderPortCall: ChemicalOrderPortCall | null,
        portCallId: number,
        imoNumber: number,
        chemicalList: Chemical[]
    ): ChemicalOrderUpdateReportData => {
        let chemicalOrderItems: ChemicalOrderItem[] = [];

        chemicalOrderItems = chemicalList.map((chemical) => {
            return {
                chemicalOrderItemId: 0,
                chemicalOrderId: 0,
                chemicalId: chemical.chemicalId,
                robValue: 0,
                previousOrderValue: 0,
                orderValue: 0,
                comment: '',
            };
        });

        chemicalOrderReport.chemicalCategoryOrderReportItems.forEach((category) => {
            category.chemicalOrderReportItems.forEach((chemicalReportItem) => {
                const chemicalOrder = chemicalOrderItems.find((c) => c.chemicalId === chemicalReportItem?.chemical?.chemicalId);
                if (chemicalOrder) {
                    chemicalOrder.robValue = chemicalReportItem.robValue;
                    chemicalOrder.orderValue = chemicalReportItem.orderValue;
                    chemicalOrder.comment = chemicalReportItem.comment;
                }
            });
        });

        const chemicalOrderId = chemicalOrderReport.chemicalOrderId ? chemicalOrderReport.chemicalOrderId : 0;
        const lastOrderStatusId = chemicalOrderPortCall ? chemicalOrderPortCall.lastOrderStatusId : ChemicalOrderStatusEnum.Unsaved;
        const updateOrderStatusId = chemicalOrderPortCall ? chemicalOrderPortCall.updateOrderStatusId : UpdateOrderStatusEnum.OriginalFromShip;
        const createdBy = chemicalOrderPortCall?.createdBy ?? '';
        const portArrivalDateLocalTime = chemicalOrderPortCall?.portArrivalDateLocalTime ?? null;

        const chemicalOrderUpdateReportData: ChemicalOrderUpdateReportData = {
            chemicalOrderId: chemicalOrderId,
            portCallId: portCallId,
            imoNumber: imoNumber,
            lastOrderStatusId: lastOrderStatusId,
            updateOrderStatusId: updateOrderStatusId,
            portArrivalDateLocalTime: portArrivalDateLocalTime,
            createdBy: createdBy,
            chemicalOrderItemList: chemicalOrderItems,
        };

        return chemicalOrderUpdateReportData;
    };

    const getChemicalOrderForPortCallBM = (
        chemicalOrderUpdateReportData: ChemicalOrderUpdateReportData,
        deliveryPortCall: PortCall
    ): ChemicalOrderPortCallBM => {
        const washedChemicalOrderItemList = washChemicalOrderItemList(chemicalOrderUpdateReportData.chemicalOrderItemList);

        // TODO: TYPE-SAFETY - Using window.location.href directly couples this function to the browser environment and makes it untestable. Consider passing the URL as a parameter
        const orcaWebUrl = window.location.href;
        const orderLocation = deliveryPortCall.port?.portName;
        const voyageNumber = deliveryPortCall.voyageNumber1;
        const portArrivalDateLocalTime = deliveryPortCall.arrivalDate?.actualDateTimeLocal;

        const chemicalOrderPortCallBM: ChemicalOrderPortCallBM = {
            chemicalOrderId: chemicalOrderUpdateReportData.chemicalOrderId,
            parentOrderId: 0,
            portCallId: chemicalOrderUpdateReportData.portCallId,
            imoNumber: chemicalOrderUpdateReportData.imoNumber,
            // TODO: VALIDATION - orderLocation is hardcoded to empty string - verify if this field should be populated with specific order location data
            orderLocation: '',
            deliveryPortCallId: chemicalOrderUpdateReportData.portCallId,
            deliveryLocation: orderLocation ?? '',
            portArrivalDateLocalTime: portArrivalDateLocalTime ?? null,
            orcaWebUrl: orcaWebUrl,
            lastOrderStatusId: chemicalOrderUpdateReportData.lastOrderStatusId,
            updateOrderStatusId: chemicalOrderUpdateReportData.updateOrderStatusId,
            voyageNumber: voyageNumber,
            // TODO: VALIDATION - createdBy is hardcoded to empty string but could potentially use data from chemicalOrderUpdateReportData.createdBy
            createdBy: '',
            // TODO: VALIDATION - lastUpdatedBy is hardcoded to empty string but could potentially use actual user data
            lastUpdatedBy: '',
            createdUtc: new Date(),
            lastUpdatedUtc: new Date(),
            chemicalOrderItemList: createChemicalOrderItemListBM(washedChemicalOrderItemList),
            chemicalOrderStatusList: [],
        };

        return chemicalOrderPortCallBM;
    };

    const getChemicalRobForPortCallBM = (chemicalRobUpdateReportData: ChemicalRobUpdateReportData): ChemicalRobPortCallBM => {
        const washedChemicalRobItemList = washChemicalRobItemList(chemicalRobUpdateReportData.chemicalRobItemList);

        const chemicalRobPortCallBM: ChemicalRobPortCallBM = {
            chemicalRobId: chemicalRobUpdateReportData.chemicalRobId,
            portCallId: chemicalRobUpdateReportData.portCallId,
            imoNumber: chemicalRobUpdateReportData.imoNumber,
            // TODO: VALIDATION - createdBy is hardcoded to empty string but could potentially use data from chemicalRobUpdateReportData.createdBy
            createdBy: '',
            createdUtc: new Date(),
            // TODO: VALIDATION - lastUpdatedBy is hardcoded to empty string but could potentially use actual user data
            lastUpdatedBy: '',
            lastUpdatedUtc: new Date(),
            chemicalRobItemList: createChemicalRobListBM(washedChemicalRobItemList),
        };

        return chemicalRobPortCallBM;
    };

    const washChemicalOrderItemList = (chemicalOrderItemList: ChemicalOrderItem[]): ChemicalOrderItem[] => {
        const washedChemicalOrderItemList: ChemicalOrderItem[] = [];

        chemicalOrderItemList.forEach((chemicalOrderItem) => {
            if (
                chemicalOrderItem != null &&
                chemicalOrderItem?.chemicalId &&
                chemicalOrderItem?.chemicalId > 0 &&
                chemicalOrderItem.orderValue &&
                chemicalOrderItem.orderValue > 0
                // TODO: LOGIC-ERROR - This filtering logic only includes items with positive orderValue - verify if zero values should be excluded
            ) {
                washedChemicalOrderItemList.push(chemicalOrderItem);
            }
        });

        return washedChemicalOrderItemList;
    };

    const washChemicalRobItemList = (chemicalRobItemList: ChemicalRobItem[]): ChemicalRobItem[] => {
        const washedChemicalRobItemList: ChemicalRobItem[] = [];

        chemicalRobItemList.forEach((chemicalRobItem) => {
            const washedChemicalRobLocationList = washChemicalRobLocationList(chemicalRobItem.chemicalRobItemLocationList);
            if (washedChemicalRobLocationList.length > 0 && chemicalRobItem.chemicalId > 0 && chemicalRobItem.robValue > 0) {
                // TODO: FUNCTION-MUTATION - This function mutates the original chemicalRobItem by assigning chemicalRobItemLocationList, which may cause side effects
                chemicalRobItem.chemicalRobItemLocationList = washedChemicalRobLocationList;
                washedChemicalRobItemList.push(chemicalRobItem);
            }
        });

        return washedChemicalRobItemList;
    };

    const washChemicalRobLocationList = (robLocationList: RobLocation[]): RobLocation[] => {
        const washedChemicalRobLocationList: RobLocation[] = [];

        robLocationList.forEach((robLocation) => {
            if (robLocation.robLocationDropdownItemId > 0) {
                washedChemicalRobLocationList.push(robLocation);
            }
        });

        return washedChemicalRobLocationList;
    };

    const getChemicalOrderReportItems = (_chemicalsInCategoryList: Chemical[]) => {
        const _chemicalOrderReportItems: ChemicalOrderItem[] = _chemicalsInCategoryList.map((chemical) => {
            return {
                chemicalOrderItemId: 0,
                chemicalOrderId: 0,
                chemicalId: chemical.chemicalId,
                chemical: chemical,
                previousOrderValue: 0,
                orderValue: 0,
                robValue: 0,
                comment: '',
            };
        });

        return _chemicalOrderReportItems;
    };

    const generateChemicalRobReport = (
        portCallId: number,
        chemicalRobForPortCall: ChemicalRobPortCall | null,
        previousChemicalRobForPortCall: ChemicalRobPortCall | null | undefined,
        chemicalList: Chemical[],
        chemicalCategoryList: ChemicalCategory[]
    ) => {
        const _chemicalCategoryRobReportItems: ChemicalCategoryRobReportItem[] = [];

        chemicalCategoryList.forEach((chemicalCategory) => {
            const _chemicalsInCategoryList = chemicalList.filter((chemical) => chemical.categoryId === chemicalCategory.categoryId);

            const _chemicalCategoryRobReportItem: ChemicalCategoryRobReportItem = {
                category: chemicalCategory,
                chemicalRobReportItems: getChemicalRobReportItems(_chemicalsInCategoryList, chemicalRobForPortCall, previousChemicalRobForPortCall),
            };
            _chemicalCategoryRobReportItems.push(_chemicalCategoryRobReportItem);
        });

        const _chemicalRobReport: ChemicalRobReport = {
            chemicalRobId: chemicalRobForPortCall?.chemicalRobId ? chemicalRobForPortCall?.chemicalRobId : null,
            // TODO: LOGIC-ERROR - portCallId uses the parameter value even when chemicalRobForPortCall is null - verify if this is correct behavior
            portCallId: portCallId ?? null,
            imoNumber: chemicalRobForPortCall?.imoNumber ? chemicalRobForPortCall?.imoNumber : null,
            chemicalCategoryRobReportItems: _chemicalCategoryRobReportItems,
        };

        return _chemicalRobReport;
    };

    const getChemicalRobReportItems = (
        _chemicalsInCategoryList: Chemical[],
        chemicalRobForPortCall: ChemicalRobPortCall | null,
        previousChemicalRobForPortCall: ChemicalRobPortCall | null | undefined
    ) => {
        const _chemicalReportItems: ChemicalRobReportItem[] = _chemicalsInCategoryList.map((chemical) => {
            return {
                chemical: chemical,
                previousRobValue: getPreviousRobValue(chemical, previousChemicalRobForPortCall),
                robValue: getRobValue(chemical, chemicalRobForPortCall),
                chemicalRobItemLocationList: getRobLocationItems(chemical.chemicalId, chemicalRobForPortCall),
            };
        });

        return _chemicalReportItems;
    };

    const getRobLocationItems = (chemicalId: number, chemicalRobForPortCall: ChemicalRobPortCall | null) => {
        const _chemicalRobItemLocationReportItems: ChemicalRobLocationReportItem[] = [];
        const locationIndexes = 3;
        const _chemicalRobItemLocationList = chemicalRobForPortCall?.chemicalRobItemList.find(
            (chemicalRobItem) => chemicalRobItem.chemicalId === chemicalId
        );
        if (_chemicalRobItemLocationList) {
            for (let locationIndex = 0; locationIndex < locationIndexes; locationIndex++) {
                let robLocationForLocation = null;
                if (locationIndex === 0) {
                    robLocationForLocation = _chemicalRobItemLocationList?.chemicalRobItemLocationList.find(
                        (r) => r.locationIndex === locationIndex || r.locationIndex === null
                    );
                } else {
                    robLocationForLocation = _chemicalRobItemLocationList?.chemicalRobItemLocationList.find((r) => r.locationIndex === locationIndex);
                }

                if (robLocationForLocation) {
                    _chemicalRobItemLocationReportItems[locationIndex] = {
                        chemicalId: chemicalId,
                        robValue: robLocationForLocation.robValue,
                        robLocationDropdownItemId: robLocationForLocation.robLocationDropdownItemId,
                    };
                } else {
                    _chemicalRobItemLocationReportItems[locationIndex] = null;
                }
            }
        }

        return _chemicalRobItemLocationReportItems;
    };

    const generateChemicalRobUpdateReportData = (
        chemicalRobForPortCall: ChemicalRobPortCall | null,
        portCallId: number,
        imoNumber: number,
        chemicalRobId: number,
        chemicalList: Chemical[]
    ) => {
        let chemicalRobItems: ChemicalRobItem[] = [];

        chemicalRobItems = chemicalList.map((chemical) => {
            const savedChemicalRobItem = chemicalRobForPortCall?.chemicalRobItemList.find((c) => c.chemicalId === chemical.chemicalId);
            const savedChemicalRobItemLocationList: RobLocation[] = [];
            if (savedChemicalRobItem?.chemicalRobItemLocationList.length > 0) {
                savedChemicalRobItem?.chemicalRobItemLocationList.forEach((c) => {
                    const robLocation: RobLocation = {
                        robLocationDropdownItemId: c.robLocationDropdownItemId,
                        robValue: c.robValue,
                        locationIndex: c.locationIndex,
                    };
                    savedChemicalRobItemLocationList.push(robLocation);
                });
            }

            return {
                portCallId: chemicalRobForPortCall?.portCallId,
                chemicalRobItemId: 0,
                chemicalId: chemical.chemicalId,
                robValue: savedChemicalRobItem?.robValue ?? 0,
                chemicalRobItemLocationList: savedChemicalRobItemLocationList,
            };
        });

        const chemicalRobUpdateReportData: ChemicalRobUpdateReportData = {
            chemicalRobId: chemicalRobId,
            portCallId: portCallId,
            imoNumber: imoNumber,
            chemicalRobItemList: chemicalRobItems,
        };

        return chemicalRobUpdateReportData;
    };

    const getRobValue = (chemical: Chemical, chemicalRobForPortCall: ChemicalRobPortCall | null) => {
        const reportedChemicalRob = chemicalRobForPortCall?.chemicalRobItemList.find((chemicalRob) => chemicalRob.chemicalId === chemical.chemicalId);
        const reportedRobValue = reportedChemicalRob?.robValue ? reportedChemicalRob?.robValue : 0;
        return reportedRobValue;
    };

    const getPreviousRobValue = (chemical: Chemical, chemicalRobForPortCall: ChemicalRobPortCall | null | undefined) => {
        const reportedChemicalRob = chemicalRobForPortCall?.chemicalRobItemList.find((chemicalRob) => chemicalRob.chemicalId === chemical.chemicalId);
        const reportedRobValue = reportedChemicalRob?.robValue ? reportedChemicalRob?.robValue : 0;
        return reportedRobValue;
    };

    const mapChemicalList = (chemicalList: Chemical[], categoryList: ChemicalCategory[], supplierList: Supplier[], unitList: Unit[]): Chemical[] => {
        // TODO: FUNCTION-MUTATION - This function mutates the input chemicalList array directly by assigning properties, which may cause side effects
        chemicalList.map((chemical) => {
            return (
                (chemical.category = categoryList.find((c) => c.categoryId === chemical.categoryId)),
                (chemical.supplier = supplierList.find((s) => s.supplierId === chemical.supplierId)),
                (chemical.unit = unitList.find((u) => u.unitId === chemical.unitId))
            );
        });
        return chemicalList;
    };

    const initChemicalOrderPortCall = (portCallId: number, imoNumber: number): ChemicalOrderPortCall => {
        return {
            chemicalOrderId: 0,
            // TODO: VALIDATION - parentOrderId is set to 0, but null might be more semantically correct for new orders without a parent
            parentOrderId: 0,
            portCallId: portCallId,
            imoNumber: imoNumber,
            orderLocation: '',
            deliveryPortCallId: 0,
            deliveryLocation: '',
            // TODO: VALIDATION - portArrivalDateLocalTime is set to new Date() but null might be more appropriate for uninitialized data
            portArrivalDateLocalTime: new Date(),
            orcaWebUrl: '',
            lastOrderStatusId: ChemicalOrderStatusEnum.Unsaved,
            updateOrderStatusId: UpdateOrderStatusEnum.OriginalFromShip,
            voyageNumber: '',
            createdBy: '',
            createdUtc: new Date(),
            lastUpdatedBy: '',
            lastUpdatedUtc: new Date(),
            chemicalOrderItemList: [],
            chemicalOrderStatusList: [],
        };
    };

    const getMainChemicalOrders = (chemicalOrderForPortCallList: ChemicalOrderPortCall[]): ChemicalOrderPortCall[] => {
        if (chemicalOrderForPortCallList && chemicalOrderForPortCallList.length > 0) {
            return (
                chemicalOrderForPortCallList
                    .sort((a, b) => (isDateBefore(a.createdUtc, b.createdUtc) ? 1 : -1))
                    // TODO: LOGIC-ERROR - Filtering by parentOrderId === 0 assumes main orders have parentOrderId of 0, not null
                    .filter((c) => c.parentOrderId === 0)
            );
        } else {
            return [];
        }
    };

    const getLatestUpdatedChemicalOrder = (chemicalOrderForPortCallList: ChemicalOrderPortCall[]): ChemicalOrderPortCall | undefined => {
        if (
            chemicalOrderForPortCallList &&
            chemicalOrderForPortCallList.length > 0 &&
            chemicalOrderForPortCallList.filter((c) => c.parentOrderId === 0).length > 0
        ) {
            return chemicalOrderForPortCallList
                .sort((a, b) => (isDateBefore(a.lastUpdatedUtc, b.lastUpdatedUtc) ? 1 : -1))
                .filter((c) => c.parentOrderId === 0)[0];
        } else {
            // TODO: RETURN-TYPE - Function returns undefined instead of null for consistency with other similar functions
            return undefined;
        }
    };

    return {
        mapChemicalList,
        generateChemicalOrderReport,
        generateChemicalRobReport,
        getChemicalOrderForPortCallBM,
        getChemicalRobForPortCallBM,
        initChemicalOrderPortCall,
        generateChemicalOrderUpdateReportData,
        generateChemicalRobUpdateReportData,
        getMainChemicalOrders,
        getLatestUpdatedChemicalOrder,
    };
};
